import * as Sentry from '@sentry/vue'

const SENSITIVE_KEY_RE = /(authorization|cookie|password|passwd|secret|token|api[_-]?key|session|dsn)/i

function parseSampleRate(value) {
  if (value === undefined || value === null || value === '') return 0
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

function scrubValue(value, depth = 0) {
  if (depth > 3) return '[redacted]'
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1))
  if (!value || typeof value !== 'object') return value

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY_RE.test(key) ? '[redacted]' : scrubValue(item, depth + 1),
    ]),
  )
}

export function stripUrlParameters(value) {
  if (typeof value !== 'string' || !value) return value
  const cut = value.search(/[?#]/)
  return cut === -1 ? value : value.slice(0, cut)
}

export function scrubEvent(event) {
  if (event.request) {
    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (SENSITIVE_KEY_RE.test(key)) event.request.headers[key] = '[redacted]'
      }
    }
    if (event.request.cookies) event.request.cookies = '[redacted]'
    if (event.request.data) event.request.data = scrubValue(event.request.data)
    if (event.request.query_string) event.request.query_string = '[redacted]'
    if (event.request.url) event.request.url = stripUrlParameters(event.request.url)
  }

  if (event.user) delete event.user
  if (event.extra) event.extra = scrubValue(event.extra)
  if (event.contexts) event.contexts = scrubValue(event.contexts)
  return event
}

export function scrubBreadcrumb(breadcrumb) {
  if (breadcrumb.data) {
    breadcrumb.data = scrubValue(breadcrumb.data)
    if (breadcrumb.data.url) breadcrumb.data.url = stripUrlParameters(breadcrumb.data.url)
    if (breadcrumb.data.from) breadcrumb.data.from = stripUrlParameters(breadcrumb.data.from)
    if (breadcrumb.data.to) breadcrumb.data.to = stripUrlParameters(breadcrumb.data.to)
  }
  return breadcrumb
}

export function buildSentryIntegrations(defaultIntegrations) {
  const kept = (Array.isArray(defaultIntegrations) ? defaultIntegrations : [])
    .filter((integration) => integration?.name !== 'BrowserSession')
  return [...kept, Sentry.browserSessionIntegration({ lifecycle: 'page' })]
}

let sentryInitialized = false

export function initSentry(app, { router, serviceName = 'admin-ui' } = {}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return false
  if (sentryInitialized) return true

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    integrations: buildSentryIntegrations,
    initialScope: {
      tags: {
        service: serviceName,
      },
    },
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  })

  sentryInitialized = true
  return true
}
